import React from 'react';
import { Node as SlateNode } from 'slate';
import { ReactEditor } from 'slate-react';
import { getStyle } from '../../Utils/styleConversion';

// Read-only TOC + placeholder renderers for the compare panes (RES-561 item 2,
// plan-compareDocuments.md §3.12 B option 2A). The compare Raw path installs only the
// generic addins, so a stored `table-of-contents` node fell through to
// DefaultElement: bare titles, no dot leaders, no page numbers, and the trailing
// `placeholder` child visible (the PDF skips it — FlowPageText.java).
//
// The stored data already carries everything the PDF renders — `toc-label` nodes
// persist their computed `page`, the toc node its `levels` (indent) and `style`
// (leader gap) — so this mirrors contents/TableOfContentsLine.js presentationally,
// minus the editor chrome (scroll-to-heading, context menu). Unlike the editor,
// the title renders the SLATE children (not element text), so diff leaf marks
// annotate TOC entries like any other text.

const CompareTocLine = ({ attributes, children, element, tocNode, styles, flowStyles }) => {
	const tocStyleName = flowStyles ? flowStyles['tableOfContent'] : null;
	const tocStyle = tocStyleName && styles ? styles[tocStyleName] : null;
	const tocStyleMerged = tocStyle ? getStyle(tocStyle, true) : {};
	const level = tocNode && tocNode.levels
		? tocNode.levels.find((l) => (
			element.heading === 'heading-one' ? l.type === 'h1'
				: element.heading === 'heading-two' ? l.type === 'h2'
					: element.heading === 'heading-three' ? l.type === 'h3'
						: false
		))
		: null;
	const indent = level ? level.indent : 0;
	// The toc node's `style` is the leader gap style. The PDF (FlowPageText.java,
	// the parity target) draws the leader ONLY for exactly 'dotted' or 'solid' —
	// 'transparent', absent, or any other value draws nothing. (The editor's
	// TableOfContentsLine defaults an absent value to solid; the PDF wins here.)
	const gapStyle = tocNode ? tocNode.style : null;
	const drawsLeader = gapStyle === 'dotted' || gapStyle === 'solid';
	const pStyleName = flowStyles ? flowStyles['p'] : null;
	const leaderColour = drawsLeader
		? (pStyleName && styles && styles[pStyleName] && styles[pStyleName].color) || 'currentColor'
		: 'transparent';
	return (
		<p
			{...attributes}
			className="compare-toc-line"
			style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', marginLeft: indent + 'pt', marginBottom: tocStyleMerged.paddingBelowParagraph }}
		>
			<span style={{ ...tocStyleMerged, width: 'fit-content' }}>{children}</span>
			<span contentEditable={false} style={{ width: 'auto', display: 'flex', flexGrow: 1, alignItems: 'baseline' }}>
				<span className="compare-toc-leader" style={{ borderBottom: `1px ${gapStyle === 'dotted' ? 'dotted' : 'solid'} ${leaderColour}`, flexGrow: 1, marginLeft: '3pt', marginRight: '3pt' }} />
				<span className="slate-toc-number" style={{ ...tocStyleMerged }}>{element ? element.page : null}</span>
			</span>
		</p>
	);
};

// Same {install → getRenderer} shape as the built-in addins; the engine consults
// every addin (use3SkyeEditor renderElement), so injecting via additionalAddins
// reaches these types without touching the package.
export default function compareTocAddin({ styles, flowStyles }) {
	return {
		install: (editor) => ({
			getRenderer: (type, theEditor) => {
				if (type === 'table-of-contents')
					return ({ attributes, children }) => (
						<div {...attributes} className="compare-toc">{children}</div>
					);
				if (type === 'toc-label')
					return ({ attributes, children, element }) => {
						// The label's indent/gap settings live on its parent toc node.
						let tocNode = null;
						try {
							const path = ReactEditor.findPath(theEditor || editor, element);
							tocNode = SlateNode.parent(theEditor || editor, path);
						} catch (e) {
							// Orphaned label — render it without the parent's settings.
						}
						return (
							<CompareTocLine
								attributes={attributes}
								children={children}
								element={element}
								tocNode={tocNode}
								styles={styles}
								flowStyles={flowStyles}
							/>
						);
					};
				if (type === 'placeholder')
					// Hidden, exactly as the FlowingPage editor and the PDF treat it.
					return ({ attributes, children }) => (
						<p {...attributes} style={{ display: 'none' }} contentEditable={false}>{children}</p>
					);
				return null;
			},
			editor,
		}),
	};
}

// The addin list a compare Slate block actually mounts with — CompareBlock and the
// TOC guard test both call THIS (liveBlockRenderGate's pattern), so the test always
// exercises the real wiring: drop the TOC addin here and the guard fails.
export function compareSlateAddins(additionalAddins, templateStyles, blockConfig) {
	return (additionalAddins || []).concat(compareTocAddin({
		styles: templateStyles,
		flowStyles: blockConfig ? blockConfig.styles : null,
	}));
}
